/**
 * V2 UI store — persisted theme/accent/density + the master V2 toggle.
 *
 * Persisted to localStorage under 'aiea-ui-store'. This is the single
 * source of truth for whether the V2 interface is active and how it is
 * themed. The classic UI never reads this store.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type V2Theme = 'dark' | 'light';
export type V2Accent = 'lime' | 'blue' | 'cyan';
export type V2Density = 'compact' | 'regular' | 'comfy';
/**
 * Active white-label brand. Re-skins the V2 app via `data-brand` on <html>.
 * Defined here (rather than registry.ts) so the store has no import cycle with
 * the registry, which itself reads V2Theme from this module.
 */
export type V2Brand = 'standard' | 'citizens' | 'statefarm';

/** Shape of the persisted V2 UI state plus its actions. */
export interface UiState {
  /** When true, the authenticated shell renders the V2 interface. */
  v2Enabled: boolean;
  /** Dark (default identity) or light theme. */
  theme: V2Theme;
  /** Signature accent applied via the `data-accent` attribute. */
  accent: V2Accent;
  /** Row density; maps to `--row-h` via DENSITY_H in useApplyTheme. */
  density: V2Density;
  /**
   * Active white-label brand, applied via `data-brand` on <html>. Persisted so
   * the attribute can be seeded synchronously on hydrate (avoids a flash);
   * a carrier role overrides this at runtime (see App.tsx).
   */
  brand: V2Brand;
  /**
   * When true, the Supplement export shows a "this will use N credits"
   * confirmation before charging. The dialog's "Don't show this again"
   * checkbox sets this false; re-enable it from Settings → Preferences.
   */
  exportCostConfirmEnabled: boolean;
  /**
   * B-499: when true, the import source preview adds a "View in source PDF"
   * action that opens the actual PDF page with the line highlighted. ON by
   * default (founder-confirmed live 2026-06-30); turn it OFF in Settings →
   * Appearance to fall back to the B-488 clean table only.
   */
  pdfViewerEnabled: boolean;

  /** Switch the authed shell over to V2. */
  enableV2: () => void;
  /** Return to the classic interface (theme attributes are removed). */
  disableV2: () => void;
  /** Set the active theme. */
  setTheme: (_theme: V2Theme) => void;
  /** Set the active accent. */
  setAccent: (_accent: V2Accent) => void;
  /** Set the active row density. */
  setDensity: (_density: V2Density) => void;
  /** Set the active white-label brand. */
  setBrand: (_brand: V2Brand) => void;
  /** Toggle the pre-conversion cost confirmation dialog. */
  setExportCostConfirmEnabled: (_enabled: boolean) => void;
  /** Toggle the B-499 source-PDF viewer (default off). */
  setPdfViewerEnabled: (_enabled: boolean) => void;
}

/**
 * Zustand store for V2 appearance + the V2 enable toggle.
 *
 * @returns The persisted UI store hook.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      v2Enabled: false,
      theme: 'dark',
      accent: 'lime',
      density: 'regular',
      brand: 'standard',
      exportCostConfirmEnabled: true,
      pdfViewerEnabled: true,

      enableV2: () => set({ v2Enabled: true }),
      disableV2: () => set({ v2Enabled: false }),
      setTheme: (theme) => set({ theme }),
      setAccent: (accent) => set({ accent }),
      setDensity: (density) => set({ density }),
      setBrand: (brand) => set({ brand }),
      setExportCostConfirmEnabled: (exportCostConfirmEnabled) =>
        set({ exportCostConfirmEnabled }),
      setPdfViewerEnabled: (pdfViewerEnabled) => set({ pdfViewerEnabled }),
    }),
    { name: 'aiea-ui-store' },
  ),
);
